import React, { Suspense } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import Clarity from '@microsoft/clarity';
import CookieConsent from './components/CookieConsent';
import { AuthProvider as CognitoAuthProvider } from 'react-oidc-context';
import ShonaReader from './shona-tts/ShonaReader';
import { lazyPage } from './utils/lazyPage';

// Only the landing page (/shonareader, which / redirects to) is in the first
// download. Every other page is its own chunk, fetched when its route is
// first visited. Imported eagerly, they made the first download 761 KB
// gzipped (jspdf, MUI, Firestore, date pickers...) for a page that uses none
// of them.
const Services = lazyPage(() => import('./ServicesApp/Services'));
const Mbira = lazyPage(() => import('./components/Mbira'));
const Dress = lazyPage(() => import('./components/auto-dress-design/Dress'));
const RacingGame = lazyPage(() => import('./components/racing/racing'));
const PongGameIndex = lazyPage(() => import('./components/pong/PongGameIndex'));
const DailyChecklist = lazyPage(
  () => import('./components/daily/DailyChecklist')
);
const WorkoutLog = lazyPage(() => import('./components/workout/WorkoutLog'));
const Login = lazyPage(() => import('./components/authentication/Login'));
const Signup = lazyPage(() => import('./components/authentication/Signup'));
const Home = lazyPage(() => import('./components/Home'));
const BreakerGameIndex = lazyPage(
  () => import('./components/pong-breaker/breakerIndex')
);
const ComingSoon = lazyPage(() => import('./components/ComingSoon'));
const ServiceSignUp = lazyPage(() => import('./ServicesApp/ServiceSignUp'));
const DailyCost = lazyPage(() => import('./components/daily-cost/DailyCost'));
const FamilyTreeAppSignInPage = lazyPage(
  () => import('./FamilyTreeApp/FamilyTreeAppSignInPage')
);
const ShonaTTS = lazyPage(() => import('./shona-tts/ShonaTTS'));
const ShonaASR = lazyPage(() => import('./shona-tts/ShonaASR'));
const AccessibilityDemo = lazyPage(
  () => import('./shona-tts/AccessibilityDemo')
);
const EnglishToShonaTranslator = lazyPage(
  () => import('./shona-tts/EnglishToShonaTranslator')
);
const Offerings = lazyPage(() => import('./shona-tts/Offerings'));
const StyleTest = lazyPage(() => import('./shona-tts/StyleTest'));
const EconetIvrDemo = lazyPage(
  () => import('./shona-tts/econet-ivr/EconetIvrDemo')
);
const Business = lazyPage(() => import('./components/business/Business'));
const Analytics = lazyPage(() => import('./shona-tts/Analytics'));
const PrivacyPage = lazyPage(() => import('./shona-tts/PrivacyPage'));
const TermsPage = lazyPage(() => import('./shona-tts/TermsPage'));
const AboutUs = lazyPage(() => import('./components/AboutUs'));
const Mindmaps = lazyPage(() => import('./components/mindmaps/Mindmaps'));

// Firebase Auth, lazy for the same reason. See utils/FirebaseRoutes.js for
// why the Shona pages sit outside it.
const loadFirebaseRoutes = () => import('./utils/FirebaseRoutes');
const FirebaseLayout = lazyPage(() =>
  loadFirebaseRoutes().then(m => ({ default: m.FirebaseLayout }))
);
const RequireFirebaseUser = lazyPage(() =>
  loadFirebaseRoutes().then(m => ({ default: m.RequireFirebaseUser }))
);

const appOrigin =
  typeof window !== 'undefined' ? window.location.origin : 'https://dzose.com';
const familyTreeRedirectUri = `${appOrigin}/family-tree`;

const cognitoAuthConfig = {
  authority: 'https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_dP1Axkrx4',
  client_id: '13detelp825o4lv95i6sjkkrm9',
  redirect_uri: familyTreeRedirectUri,
  response_type: 'code',
  scope: 'email openid phone',
  // Same as /shonareader: drop the ?code=&state= params once the redirect has
  // been handled, so a refresh does not re-run the callback.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, familyTreeRedirectUri);
  },
};

// Shona Reader has its own Cognito user pool (separate user base from the
// family tree). Guests get a limited number of free generations before they
// must sign up; signed-in users are unlimited.
const shonaReaderRedirectUri = `${appOrigin}/shonareader`;

const shonaReaderAuthConfig = {
  authority: 'https://cognito-idp.eu-west-1.amazonaws.com/eu-west-1_5XV7VquTS',
  client_id: '1qc368j9l2rkh340ks88t788s5',
  redirect_uri: shonaReaderRedirectUri,
  response_type: 'code',
  scope: 'email openid phone',
  // Renew 11 minutes before expiry, not the default 1. Modal saves a finished
  // clip to History with the token it was sent, up to its 10-minute request
  // limit later, so that token has to outlast the generation.
  accessTokenExpiringNotificationTimeInSeconds: 660,
  // Strip the ?code=&state= params from the URL after a successful login so
  // refreshes don't re-trigger the callback handler.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, shonaReaderRedirectUri);
  },
};

// The /analytics dashboard reuses the same Shona Reader user pool but must
// return to /analytics after sign-in (the page itself gates to the owner's
// email). Same authority + client_id means the OIDC session is shared with
// /shonareader, so signing in on either page carries over to the other.
const analyticsRedirectUri = `${appOrigin}/analytics`;

const analyticsAuthConfig = {
  ...shonaReaderAuthConfig,
  redirect_uri: analyticsRedirectUri,
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, analyticsRedirectUri);
  },
};

//test

// Started after `load`, so Clarity's script does not download and run while
// the page itself is still starting up on a slow connection.
const startClarity = () => Clarity.init('s8gky26urq'); // make this only render only when not in localhost:3000
if (document.readyState === 'complete') {
  startClarity();
} else {
  window.addEventListener('load', startClarity, { once: true });
}

function App() {
  return (
    <Router>
      {/* Blank while a page's chunk downloads, as these pages were blank
          before while Firebase reported the auth state. */}
      <Suspense fallback={null}>
        <Routes>
          {/* ── Shona pages: no Firebase ── */}
          <Route path="/" element={<Navigate to="/shonareader" replace />} />
          <Route
            path="/shonareader"
            element={
              <CognitoAuthProvider {...shonaReaderAuthConfig}>
                <ShonaReader />
              </CognitoAuthProvider>
            }
          />
          <Route
            path="/ShonaReader"
            element={
              <CognitoAuthProvider {...shonaReaderAuthConfig}>
                <ShonaReader />
              </CognitoAuthProvider>
            }
          />
          {/* Owner-only dashboard, gated to the owner's email inside the
              component. Needs the local analytics server (npm run analytics,
              AWS profile `personal`). */}
          <Route
            path="/analytics"
            element={
              <CognitoAuthProvider {...analyticsAuthConfig}>
                <Analytics />
              </CognitoAuthProvider>
            }
          />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/shona-tts" element={<ShonaTTS />} />
          {/* Internal palette preview for the shona-tts pages. Unlinked and
              not in the sitemap; delete with StyleTest.jsx once a palette is
              picked (theme.js is the part that stays). */}
          <Route path="/style-test" element={<StyleTest />} />
          <Route path="/shona-asr" element={<ShonaASR />} />
          <Route path="/accessibily" element={<AccessibilityDemo />} />
          <Route path="/accessibility" element={<AccessibilityDemo />} />
          <Route
            path="/to-shona-translater-with-audio"
            element={
              <CognitoAuthProvider {...shonaReaderAuthConfig}>
                <EnglishToShonaTranslator />
              </CognitoAuthProvider>
            }
          />
          {/* Legacy paths — keep old links working */}
          <Route
            path="/english-to-shona"
            element={<Navigate to="/to-shona-translater-with-audio" replace />}
          />
          <Route
            path="/english-to-shona-translator"
            element={<Navigate to="/to-shona-translater-with-audio" replace />}
          />
          <Route path="/ivr-demo" element={<EconetIvrDemo />} />
          {/*** <Route path="/offerings" element={<Offerings />} />****/}

          {/* ── Everything else: inside Firebase Auth, as before ── */}
          <Route element={<FirebaseLayout />}>
            <Route path="/serviceold" element={<Services />} />
            <Route path="/mbira" element={<Mbira />} />
            <Route path="/auto-dress" element={<Dress />} />
            <Route path="/racing" element={<RacingGame />} />
            <Route path="/pong" element={<PongGameIndex />} />
            <Route path="/breaker" element={<BreakerGameIndex />} />

            {/***<Route path="/" element={<Home />} />***/}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/coming-soon" element={<ComingSoon />} />
            <Route
              path="/family-tree"
              element={
                <CognitoAuthProvider {...cognitoAuthConfig}>
                  <FamilyTreeAppSignInPage />
                </CognitoAuthProvider>
              }
            />
            <Route path="/service-signup" element={<ServiceSignUp />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/mindmaps" element={<Mindmaps />} />

            <Route element={<RequireFirebaseUser />}>
              <Route path="/daily" element={<DailyChecklist />} />
              <Route path="/workout" element={<WorkoutLog />} />
              <Route path="/daily-cost" element={<DailyCost />} />
              <Route path="/business" element={<Business />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <CookieConsent />
    </Router>
  );
}

export default App;
